import { useIntl } from 'react-intl'
import _get from 'lodash/get';
import { useGetRewardsStatsQuery } from '@/shared/api/Stats';
import { useSelector } from 'react-redux';
import { selectPollingInterval } from '@/processes/Settings';
import { useCurrencyWithExchange, useCryptoFormatter, useThreshold } from '@/processes/PoolStats';
import CoinStats from '@/entities/CoinStats';
import { useMinerAccount } from "@/processes/MinerAccounts";
import Tooltip from '@/shared/ui/Tooltip';
import { IconInfo } from '@/shared/ui/Icons';
import { toFixed } from '@/shared/utils/toFixed';
import { usePayoutData } from '@/shared/hooks/usePayoutData';

type BalanceProps = {
  className?: string;
}

const Balance = (props: BalanceProps) => {
  const {
    className = '',
  } = props;
  const intl = useIntl();
  const pollingInterval = useSelector(selectPollingInterval);
  const { account, miner, coin } = useMinerAccount();
  const isLTCBTC = coin === 'LTCBTC';
  
  const payoutData = usePayoutData();
  const { threshold } = useThreshold({ coin: isLTCBTC ? 'BTC' : coin })
  const { formatCurrency } = useCurrencyWithExchange({ coin: isLTCBTC ? 'BTC' : coin })
  const { formatCrypto, addCoinPostfix } = useCryptoFormatter({ coin: isLTCBTC ? 'BTC' : coin })
  const { data: rewardsStats = {}, isSuccess } = useGetRewardsStatsQuery(
    { account, coin, miner }, 
    { pollingInterval, skip: !(account || miner) }
  );

  const actualThreshold = _get(payoutData, 'threshold', threshold) || threshold;

  // const unpaid = _get(rewardsStats, 'unpaid', 0); // This is for BTC
  const altcoinUnpaid = _get(rewardsStats, 'altcoinUnpaid', 0);

  const available = toFixed(_get(rewardsStats, 'available', 0));
  const locked = toFixed(_get(rewardsStats, 'locked', 0));
  const estimated = toFixed(_get(rewardsStats, 'estimated', 0));

  return (
    <CoinStats
      className={className}
      isLoading={!(isSuccess)}
      coin={coin}
      label={
        <Tooltip
          ToggleComponent={
            <span className="flex items-center">
              <span className="mr-1">
                {intl.formatMessage({ id: 'stats.unpaid' })}
              </span>
              <IconInfo/>
            </span>
          }
        >
          <ul>
            <li className="mb-1 leading-5">
              {intl.formatMessage({ id: 'stats.payoutHint.gram.1' }, { available, locked, estimated })}
            </li>
            <li className="mb-1 leading-5">
              {intl.formatMessage({ id: 'stats.payoutHint.gram.2' }, {
                available,
                br: <br/>,
                b: (...chunks) => <b>{chunks}</b>
              })}
            </li>
            <li className="mb-1 leading-5">
              {intl.formatMessage({ id: 'stats.payoutHint.gram.3' }, {
                locked,
                br: <br/>,
                b: (...chunks) => <b>{chunks}</b>
              })}
            </li>
            <li className="mb-1 leading-5">
              {intl.formatMessage({ id: 'stats.payoutHint.gram.4' }, {
                estimated,
                br: <br/>,
                b: (...chunks) => <b>{chunks}</b>
              })}
            </li>
          </ul>
        </Tooltip>
      }
      value={formatCrypto(altcoinUnpaid)}
      subValue={addCoinPostfix(actualThreshold)}
      tip={formatCurrency(altcoinUnpaid)}
    />
  );
}

export default Balance;
